import type { UseFormReturn } from 'react-hook-form';
import { Box } from '@mui/material';
import { Accordion, RhfFractionField, RhfTextField } from '@core/components';
import type { Vestment } from '../../../../api/vestment';
import { ChasublePanelMaterialFields } from '../../../components/ChasubleValueFields';
import { useAppContext } from '../../../context/AppContext';
import { VestmentCategory } from '../../../enum/vestmentCategory';
import { useCustomerById } from '../../../hooks/useCustomerById';
import { getChasubleFeatures } from '../../../utils/chasuble';
import { formatMeasurement } from '../../../utils/measurement';
import {
  type ChasubleItemFormValues,
  defaultPanelLength,
  defaultPanelWidth,
} from '../../../utils/orderItem';
import { getPresetSizeForCategory } from '../../../utils/size';

const rowSx = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 2,
} as const;

interface PanelSectionProps {
  methods: UseFormReturn<ChasubleItemFormValues>;
  vestment?: Vestment;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
}

export function PanelSection({
  methods,
  vestment,
  expanded,
  onChange,
}: PanelSectionProps) {
  const { sizes } = useAppContext();

  const design = methods.watch('design');
  const sizeId = methods.watch('size');
  const forCustomerId = methods.watch('forCustomer');
  const length = methods.watch('length');
  const centerFinish = methods.watch('centerFinish');

  const vestmentType = vestment?.type;
  const selectedSize = getPresetSizeForCategory(
    sizes,
    VestmentCategory.Chasuble,
    sizeId
  );
  const { data: forCustomer } = useCustomerById(forCustomerId);

  return (
    <Accordion
      title="Panel"
      expanded={expanded}
      collapsed={getChasubleFeatures(vestmentType)?.panel === false}
      onChange={onChange}
    >
      <ChasublePanelMaterialFields methods={methods} source={vestmentType}>
        {(fields) => (
          <Box sx={rowSx}>
            {fields.has('dimension') ? (
              <RhfTextField<ChasubleItemFormValues>
                name="panelDimension"
                label="Dimension"
              />
            ) : null}
            {fields.has('width') ? (
              <RhfFractionField<ChasubleItemFormValues>
                name="panelWidth"
                label="Width (in)"
                placeholder={
                  formatMeasurement(
                    defaultPanelWidth(centerFinish, design?.centerFinish)
                  ) ?? undefined
                }
              />
            ) : null}
            <RhfFractionField<ChasubleItemFormValues>
              name="panelLength"
              label="Length (in)"
              placeholder={
                formatMeasurement(
                  defaultPanelLength({
                    customer: forCustomer,
                    size: selectedSize,
                    vestmentType,
                    vestmentCategory: VestmentCategory.Chasuble,
                    lineItemLength: length,
                  })
                ) ?? undefined
              }
            />
            {fields.has('stoleSize') ? (
              <RhfTextField<ChasubleItemFormValues>
                name="stoleSize"
                label="Stole Size"
              />
            ) : null}
            {!fields.has('dimension') &&
            !fields.has('width') &&
            !fields.has('stoleSize') ? (
              <Box
                sx={{ width: '100%', display: { xs: 'none', md: 'block' } }}
              />
            ) : null}
          </Box>
        )}
      </ChasublePanelMaterialFields>
    </Accordion>
  );
}
